import { CalendarIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import es from 'date-fns/locale/es';
import { forwardRef, LegacyRef, memo, MouseEvent, useCallback, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { useTranslation } from 'react-i18next';

import { IconSvg } from '@/lib/v2/components';
import { withController } from '@/lib/v2/HOCs/WithController';

import CustomHeader from './CustomHeader';
import DateInputContainer from './DateInputContainer';

import 'react-datepicker/dist/react-datepicker.css';
import './DateInput.tailwind.css';

registerLocale('es', es);

export type eventOnChangeDateInput = (date: Date | [Date | null, Date | null] | null) => void;

export interface DateInputProps {
  id?: string;
  disabled?: boolean;
  date?: Date | null;
  name?: string;
  label?: string;
  dateFormat?: string;
  placeholder?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
  error?: boolean;
  message?: string;
  onChange?: eventOnChangeDateInput;
  onDelete?: (indexDate: number) => void;
  selectsRange?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  dates?: Date[] | string[];
  multiDates?: boolean;
  readOnly?: boolean;
  iconColor?: 'secondary' | 'primary';
  isRequired?: boolean;
  /** show skeleton loading */
  isLoading?: boolean;
  fullWidth?: boolean;
  showTimeSelect?: boolean;
  showTimeInput?: boolean;
  showTimeSelectOnly?: boolean;
  timeFormat?: string;
  showMonthYearPicker?: boolean;
}

const DateInput = forwardRef(
  (
    {
      date,
      dates,
      onChange,
      onDelete,
      id,
      label,
      name,
      dateFormat = 'dd/MM/yyyy',
      placeholder = 'dd/MM/yyyy',
      disabled = false,
      error,
      message,
      selectsRange = false,
      startDate,
      endDate,
      minDate,
      maxDate,
      multiDates,
      readOnly = false,
      iconColor = 'secondary',
      isRequired,
      isLoading,
      fullWidth = false,
      showTimeSelect,
      showTimeInput,
      showTimeSelectOnly,
      timeFormat,
      showMonthYearPicker,
    }: DateInputProps,
    ref?: LegacyRef<DatePicker<never, boolean>>
  ): JSX.Element => {
    const [selected, setSelected] = useState(false);

    const divRef = useRef() as React.MutableRefObject<HTMLDivElement>;

    const { i18n } = useTranslation();

    const handleOnChange = (dateValue: Date) => {
      onChange?.(dateValue);
      multiDates && setSelected(false);
    };

    const classesContainer = classNames('date-input--container', {
      '!w-full': fullWidth,
    });

    const classesMessage = classNames({ 'message-error': error });
    const classesContainerInput = classNames('relative rounded-md', {
      'date-input--container-error': error,
      readOnly: readOnly,
    });
    const classesDiv = classNames('eb-date-input--multi-dates', 'relative overflow-x-hidden', {
      '!border-emblue-primary': selected,
    });
    const iconDisabledClasses = classNames({ 'opacity-50': disabled });

    const deleteValue = useCallback(
      (index: number) => (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onDelete?.(index);
      },
      [onDelete]
    );

    const multiDatesElements =
      Array.isArray(dates) &&
      dates.map((val, index) => (
        <span
          key={index}
          className="ml-0.5 inline-flex items-center rounded-full bg-blue-50 pl-2 pr-0.5
                text-base font-medium text-emblue-primary"
        >
          {val instanceof Date ? val.toISOString().split('T')[0] : val}
          <button
            className="ml-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full text-emblue-primary
                    hover:bg-indigo-200 hover:text-emblue-primary-hover focus:bg-blue-400
                    focus:text-white focus:outline-none"
            id="delete-value-button"
            type="button"
            onClick={deleteValue(index)}
          >
            <span className="sr-only">Remove small option</span>
            <svg className="size-2" fill="none" stroke="currentColor" viewBox="0 0 8 8">
              <path d="M1 1l6 6m0-6L1 7" strokeLinecap="round" strokeWidth="1.5" />
            </svg>
          </button>
        </span>
      ));

    if (isLoading) {
      return (
        <div className="flex w-full animate-pulse flex-col" data-testid="select-component">
          <div className="mt-1.5 h-2.5 w-28 rounded-full bg-gray-200"></div>
          <div className="my-1 h-[38px] w-full rounded bg-gray-200"></div>
        </div>
      );
    }

    return (
      <div className={classesContainer} {...(id && { id })} data-testid="date-input-component">
        {label && (
          <label className="label" htmlFor={name}>
            {label} {isRequired && <span className="text-red-500"> * </span>}
          </label>
        )}
        <div ref={divRef} className={classesContainerInput} id="date-input-group">
          {multiDates && (
            <div
              aria-hidden
              className={classesDiv}
              role="textbox"
              onClick={() => setSelected((prevSelected) => !prevSelected)}
            >
              {!readOnly && (
                <div
                  className={`${iconDisabledClasses} absolute inset-y-0 left-0 flex items-center pl-3`}
                  style={{ zIndex: 1 }}
                >
                  <IconSvg
                    height="20px"
                    strokeColor={iconColor}
                    svgComponent={
                      <CalendarIcon aria-hidden="true" className="size-5 text-gray-400" />
                    }
                    width="20px"
                  />
                </div>
              )}
              <div className="eb-multi-dates--div flex flex-row text-[#A7B1CC]">
                {dates && dates?.length > 0 ? multiDatesElements : placeholder}
              </div>
            </div>
          )}
          {!multiDates && !readOnly && (
            <div
              className={`${iconDisabledClasses} absolute inset-y-0 left-0 flex items-center pl-3`}
              style={{ zIndex: 1 }}
            >
              <IconSvg
                height="20px"
                strokeColor={iconColor}
                svgComponent={<CalendarIcon aria-hidden="true" className="size-5 text-gray-400" />}
                width="20px"
              />
            </div>
          )}
          {!multiDates && (
            <DatePicker
              ref={ref}
              renderCustomHeader={(props) => (
                <CustomHeader
                  maxDate={maxDate}
                  minDate={minDate}
                  showMonthYearPicker={showMonthYearPicker}
                  {...props}
                />
              )}
              {...(!selectsRange && { selected: date })}
              closeOnScroll
              calendarContainer={DateInputContainer}
              dateFormat={dateFormat}
              disabled={disabled}
              locale={i18n.language}
              name={name}
              placeholderText={placeholder}
              selectsRange={selectsRange}
              onChange={handleOnChange}
              {...(selectsRange && { startDate })}
              {...(selectsRange && { endDate })}
              autoComplete="off"
              className={label ? 'my-1' : ''}
              maxDate={maxDate}
              minDate={minDate}
              readOnly={readOnly}
              showMonthYearPicker={showMonthYearPicker}
              showTimeInput={showTimeInput}
              showTimeSelect={showTimeSelect}
              showTimeSelectOnly={showTimeSelectOnly}
              timeFormat={timeFormat}
            />
          )}
          {multiDates && selected && (
            <div
              className="z-10"
              style={{
                position: 'absolute',
                inset: '0px auto auto 0px',
                transform: 'translate(0px,46px)',
              }}
            >
              <DatePicker
                ref={ref}
                renderCustomHeader={(props) => (
                  <CustomHeader
                    maxDate={maxDate}
                    minDate={minDate}
                    showMonthYearPicker={showMonthYearPicker}
                    {...props}
                  />
                )}
                {...(!selectsRange && { selected: date })}
                closeOnScroll
                calendarContainer={DateInputContainer}
                dateFormat={dateFormat}
                disabled={disabled}
                locale={i18n.language}
                name={name}
                placeholderText={placeholder}
                selectsRange={selectsRange}
                onChange={handleOnChange}
                {...(selectsRange && { startDate })}
                {...(selectsRange && { endDate })}
                autoComplete="off"
                inline={multiDates}
                maxDate={maxDate}
                minDate={minDate}
                readOnly={readOnly}
              />
            </div>
          )}
        </div>
        {error && (
          <p className={classesMessage} id={`${name}-message`}>
            {message}
          </p>
        )}
      </div>
    );
  }
);

export default memo(withController(DateInput));

export const adjustToUTCDate = (dateString: string) => {
  const date = new Date(dateString);
  const userOffset = date.getTimezoneOffset() * 60000; // offset in milliseconds
  return new Date(date.getTime() + userOffset);
};
